'use client';

import { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { Loader2 } from 'lucide-react';
const PdfViewer = dynamic(() => import('./pdf-viewer').then((m) => m.PdfViewer), {
  ssr: false,
  loading: () => (
    <div className="relative flex-1 bg-white dark:bg-neutral-800 flex items-center justify-center">
      <div className="flex items-center gap-3 text-neutral-500 dark:text-neutral-400">
        <div className="h-5 w-5 rounded-full border-2 border-neutral-200 border-t-neutral-500 animate-spin" />
        <span className="text-sm">Se încarcă preview...</span>
      </div>
    </div>
  ),
});
import { Toolbar } from './toolbar';
import type { DocumentPreviewPanelProps } from './types';

/**
 * Modal de preview pentru documente (facturi, avize, proforme, NIR).
 *
 * Shell stateless + conținut pluggable:
 * - PDF rendering identic cu /facturi (react-pdf, zoom, paginare flotantă)
 * - Toolbar declarativ prin `toolbarActions`
 * - Sidebar prin `sidebarSections` (render props)
 *
 * Shell-ul NU cunoaște dialog-uri (PaymentDialog, SendEmailDialog etc.) —
 * acestea rămân în pagina părinte, shell-ul doar triggerează callbacks.
 */
export function DocumentPreviewPanel({
  open,
  onClose,
  isLoading = false,
  document: doc,
  pdfUrl,
  pdfError = null,
  pdfLoading = false,
  toolbarActions,
  sidebarSections,
  width = 1180,
  getDocumentTitle,
  getDocumentSubtitle,
  getDocumentStatus,
  banner = null,
  onBannerDismiss,
  viewSwitcher = null,
  instant = false,
}: DocumentPreviewPanelProps) {
  const [visible, setVisible] = useState(!!instant);
  // Sidebar scrolled? — adăugă shadow pe toolbar când sidebar a fost scroll-at
  const [sidebarScrolled, setSidebarScrolled] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // IntersectionObserver pentru sentinel la top sidebar — detectează scroll
  useEffect(() => {
    if (!open) return;
    const el = sidebarRef.current;
    if (!el) return;
    const handler = () => setSidebarScrolled(el.scrollTop > 4);
    el.addEventListener('scroll', handler, { passive: true });
    handler();
    return () => el.removeEventListener('scroll', handler);
  }, [open]);

  // Animate in on mount
  useEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, [open]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setVisible(false);
    }
  }, [open]);

  // Body scroll lock
  useEffect(() => {
    if (!open) return;
    const prev = window.document.body.style.overflow;
    window.document.body.style.overflow = 'hidden';
    return () => {
      window.document.body.style.overflow = prev;
    };
  }, [open]);

  // Escape key
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.document.addEventListener('keydown', onKey);
    return () => window.document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Auto-dismiss banner după 3s — reset timer la schimbare banner.id
  useEffect(() => {
    if (!banner) return;
    const t = setTimeout(() => onBannerDismiss?.(), 3000);
    return () => clearTimeout(t);
  }, [banner?.id, banner?.label, onBannerDismiss]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={
          'fixed inset-0 bg-black/40 backdrop-blur-sm z-[90] transition-opacity duration-200 ' +
          (visible ? 'opacity-100' : 'opacity-0')
        }
      />

      {/* Drawer — spring easing pentru entry */}
      <aside
        className={
          'fixed top-0 right-0 bottom-0 bg-white dark:bg-neutral-900 shadow-2xl z-[91] flex flex-col transition-transform duration-[320ms] ease-[cubic-bezier(0.32,0.72,0,1)] ' +
          (visible ? 'translate-x-0' : 'translate-x-full')
        }
        style={{ width: Math.min(width, typeof window !== 'undefined' ? window.innerWidth * 0.95 : width) }}
      >
        {/* Toolbar — sticky shadow când sidebar e scroll-at; banner-ul de
            confirmare apare ca pill în interiorul header-ului (vezi Toolbar). */}
        <div
          className={
            'transition-shadow duration-200 ' +
            (sidebarScrolled ? 'shadow-[0_2px_8px_-4px_rgba(0,0,0,0.08)]' : '')
          }
        >
          <Toolbar
            document={doc}
            actions={toolbarActions}
            onClose={onClose}
            getDocumentTitle={getDocumentTitle}
            getDocumentSubtitle={getDocumentSubtitle}
            getDocumentStatus={getDocumentStatus}
            banner={banner}
            onBannerDismiss={onBannerDismiss}
            viewSwitcher={viewSwitcher}
          />
        </div>

        {/* Body: PDF + Sidebar */}
        <div className="flex-1 flex overflow-hidden min-h-0">
          <PdfViewer
            pdfUrl={pdfUrl}
            pdfLoading={pdfLoading}
            pdfError={pdfError}
          />

          {/* Sidebar */}
          <div
            ref={sidebarRef}
            className="w-[340px] shrink-0 border-l border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 overflow-y-auto"
          >
            {isLoading ? (
              <div className="flex items-center justify-center h-40 gap-2 text-neutral-400 dark:text-neutral-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Se încarcă...</span>
              </div>
            ) : (
              sidebarSections
            )}
          </div>
        </div>
      </aside>
    </>
  );
}
